import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { companiesAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Building2, ChevronRight, Sun, Home } from "lucide-react";

export default function CompanySelectPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);
  const { user, selectCompany } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await companiesAPI.list();
      // Superadmins see all companies, others only see their assigned companies
      let userCompanies;
      if (user?.role === "superadmin") {
        userCompanies = response.data;
      } else {
        userCompanies = response.data.filter(c => 
          user?.company_ids?.includes(c.id)
        );
      }
      setCompanies(userCompanies);
    } catch (error) {
      console.error("Failed to load companies:", error);
      toast.error("Fehler beim Laden der Firmen");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = async (companyId) => {
    setSelecting(companyId);
    try {
      await selectCompany(companyId);
      toast.success("Firma ausgewählt");
      // Navigate to dashboard after selecting company
      navigate("/");
    } catch (error) {
      console.error("Failed to select company:", error);
      toast.error("Fehler beim Auswählen der Firma");
    } finally {
      setSelecting(null);
    }
  };

  const handleGoToDashboard = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 mesh-bg">
      <div className="w-full max-w-lg animate-slide-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="h-14 w-14 rounded-2xl bg-accent flex items-center justify-center shadow-lg">
            <Sun className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold font-['Manrope']">SolarCRM</h1>
            <p className="text-sm text-muted-foreground">PV Angebote & Verträge</p>
          </div>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-['Manrope']">Firma auswählen</CardTitle>
            <CardDescription>
              Wählen Sie die Firma, für die Sie arbeiten möchten
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {companies.map((company) => (
              <Button
                key={company.id}
                variant="outline"
                className="w-full h-auto p-4 justify-between hover:bg-muted"
                onClick={() => handleSelectCompany(company.id)}
                disabled={selecting !== null}
                data-testid={`company-select-${company.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">{company.name}</p>
                    {company.address && (
                      <p className="text-xs text-muted-foreground">{company.address}</p>
                    )}
                  </div>
                </div>
                {selecting === company.id ? (
                  <span className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
              </Button>
            ))}

            {companies.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Keine Firmen verfügbar</p>
                <p className="text-sm">Bitte kontaktieren Sie Ihren Administrator</p>
              </div>
            )}

            {/* Button zum Dashboard als Fallback - immer sichtbar */}
            <div className="pt-4 border-t mt-4">
              <Button
                variant="secondary"
                className="w-full"
                onClick={handleGoToDashboard}
                data-testid="go-to-dashboard-btn"
              >
                <Home className="h-4 w-4 mr-2" />
                Zum Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
